import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import {
  TVehiculeCheckSchema,
  vehiculeCheckSchema,
} from '@/schemas/vehicule-check/vehicule-check.schema';
import { VehiculeCheckRequestManagement } from '../components/VehiculeCheckRequestManagement';
import {
  useGetVehiculeCheckById,
  useUpdateVehiculeCheck,
} from '@/hooks/vehicule-check.hooks';
import { useTranslation } from 'react-i18next';
import { VehiculeCheckProgressStatus } from '@/api/entities/vehicule-check';
import { TVehiculeCheckEmailSchema } from '@/schemas/vehicule-check-email/vehicule-check-email.schema';
import { useEffect, useState } from 'react';
import { useCreateVehiculeCheckEmail } from '@/hooks/vehicule-check-email.hooks';
import { getPluralString } from '@/utils/string.utils';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { VehiculeCheckEmailList } from '@/modules/VehiculeCheckEmail/components/VehiculeCheckEmailList';
import { AllSelectedTableItemsProvider } from '@/providers/AllSelectedTableItemsProvider';
import { useGetTab } from '@/hooks/tab';
import { EditRequestVehiculeCheckTab } from '@/type/ui/tab/vehiculeCheckTab';
import { getLanguage } from '@/utils/language.utils';

export const EditVehiculeCheckRequestPage = () => {
  const { t } = useTranslation('inspection');
  const { id } = useParams();
  const { data, isLoading } = useGetVehiculeCheckById(`${id}`);
  const navigate = useNavigate();
  const { isFrench } = getLanguage();
  const [emailDetails, setEmailDetails] = useState<TVehiculeCheckEmailSchema>();

  const numberOfRequests = data?.data?.emails?.length;
  const pageTitle = isFrench
    ? `${getPluralString(t('request'), numberOfRequests)} ${t('ofInspection')}`
    : `${getPluralString(t('inspectionRequest'), numberOfRequests)}`;

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditRequestVehiculeCheckTab.INFOS,
  });

  useEffect(() => {
    if (location.hash === '#emailsSent') {
      navigateToTab(EditRequestVehiculeCheckTab.EMAILS_SENT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    watch: watchVehiculeCheck,
    control: vehiculeCheckControl,
    register: registerVehiculeCheck,
    setValue: setVehiculeCheckValue,
    setError: setVehiculeCheckError,
    handleSubmit: handleSubmitVehiculeCheck,
    formState: {
      errors: vehiculeCheckErrors,
      isSubmitting: isSubmittingVehiculeCheck,
    },
  } = useForm<TVehiculeCheckSchema>({
    resolver: zodResolver(vehiculeCheckSchema),
  });

  const onCreateEmail = (vehiculeCheckEmail: TVehiculeCheckEmailSchema) => {
    setEmailDetails(vehiculeCheckEmail);
  };

  const {
    mutate: mutateCreateVehiculeCheckEmail,
    isPending: isPendingCreateVehiculeCheckEmail,
  } = useCreateVehiculeCheckEmail({
    t,
    handleSuccess: () => navigate('/vehicule-checks'),
  });

  const {
    mutate: mutateUpdateVehiculeCheck,
    isPending: isPendingUpdateVehiculeCheck,
  } = useUpdateVehiculeCheck({
    t,
    handleSuccess: () => {
      if (emailDetails?.emailSubject && emailDetails?.emailContent) {
        mutateCreateVehiculeCheckEmail({
          ...emailDetails,
          vehiculeCheckId: id,
        });
      } else {
        navigate('/vehicule-checks');
      }
    },
  });

  const onSubmitVehiculeCheck = async (vehiculeCheck: TVehiculeCheckSchema) => {
    vehiculeCheck = {
      id,
      ...vehiculeCheck,
      progressStatus:
        emailDetails?.emailSubject && emailDetails?.emailContent
          ? VehiculeCheckProgressStatus.REQUEST_CREATED_AND_SENT
          : VehiculeCheckProgressStatus.REQUEST_CREATED,
    };
    mutateUpdateVehiculeCheck(vehiculeCheck);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <PageHeader
        backToLink="/vehicule-checks"
        title={pageTitle}
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem
              text={t('inspectionList')}
              linkTo="/vehicule-checks"
            />
            <BreadcrumbItem text={pageTitle} active />
          </BreadcrumbList>
        }
      />
      {!data?.data ? (
        <NotFound text={`${t('noInspectionFound')}...`} />
      ) : (
        <div>
          {numberOfRequests > 0 && (
            <TabList className="mb-5">
              <TabItem
                text={t('requestThisInspection')}
                leftIcon={<i className="ki-outline ki-badge" />}
                active={currentTab === EditRequestVehiculeCheckTab.INFOS}
                onChange={() =>
                  navigateToTab(EditRequestVehiculeCheckTab.INFOS)
                }
              />
              <TabItem
                text={t('emailsSent')}
                leftIcon={<i className="ki-outline ki-parcel" />}
                active={currentTab === EditRequestVehiculeCheckTab.EMAILS_SENT}
                onChange={() =>
                  navigateToTab(EditRequestVehiculeCheckTab.EMAILS_SENT)
                }
              />
            </TabList>
          )}
          {currentTab === EditRequestVehiculeCheckTab.INFOS && (
            <VehiculeCheckRequestManagement<TVehiculeCheckSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchVehiculeCheck}
              control={vehiculeCheckControl}
              register={registerVehiculeCheck}
              setValue={setVehiculeCheckValue}
              setError={setVehiculeCheckError}
              errors={vehiculeCheckErrors}
              submitDisabled={
                isSubmittingVehiculeCheck ||
                isPendingUpdateVehiculeCheck ||
                isPendingCreateVehiculeCheckEmail
              }
              onSubmit={handleSubmitVehiculeCheck(onSubmitVehiculeCheck)}
              onCreateEmail={onCreateEmail}
            />
          )}
          {currentTab === EditRequestVehiculeCheckTab.EMAILS_SENT && (
            <AllSelectedTableItemsProvider>
              <VehiculeCheckEmailList />
            </AllSelectedTableItemsProvider>
          )}
        </div>
      )}
      {(isSubmittingVehiculeCheck ||
        isPendingUpdateVehiculeCheck ||
        isPendingCreateVehiculeCheckEmail) && <Loader />}
    </>
  );
};
